import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/admin/AdminLayout';
import { pagesApi } from '../../services/api';

function AdminPages() {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    loadPages();
  }, []);

  const loadPages = async () => {
    try {
      const response = await pagesApi.getAll(true); // Include inactive
      setPages(response.data);
    } catch (error) {
      console.error('Error loading pages:', error);
      alert('Failed to load pages');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (pageId) => {
    navigate(`/admin/pages/${pageId}/edit`);
  };

  const handleDelete = async (pageId) => {
    if (!window.confirm('Are you sure you want to delete this page? This action cannot be undone.')) {
      return;
    }

    try {
      await pagesApi.delete(pageId);
      alert('Page deleted successfully!');
      loadPages(); // Reload the list
    } catch (error) {
      console.error('Error deleting page:', error);
      alert('Failed to delete page');
    }
  };

  const toggleStatus = async (pageId, currentStatus) => {
    try {
      const page = pages.find(p => p.id === pageId);
      if (!page) return;

      await pagesApi.update(pageId, {
        ...page,
        is_active: !currentStatus
      });

      alert(`Page ${!currentStatus ? 'activated' : 'deactivated'} successfully!`);
      loadPages(); // Reload the list
    } catch (error) {
      console.error('Error updating page status:', error);
      alert('Failed to update page status');
    }
  };

  const viewPage = (slug) => {
    window.open(`/${slug}`, '_blank');
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Pages Management</h1>
            <p className="text-gray-600">Manage your website pages</p>
          </div>
          <Link
            to="/admin/pages/new"
            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
          >
            + Add New Page
          </Link>
        </div>

        {/* Stats Summary */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg shadow p-4">
            <p className="text-sm text-gray-600">Total Pages</p>
            <p className="text-2xl font-bold">{pages.length}</p>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <p className="text-sm text-gray-600">Active Pages</p>
            <p className="text-2xl font-bold text-green-600">
              {pages.filter(p => p.is_active).length}
            </p>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <p className="text-sm text-gray-600">Inactive Pages</p>
            <p className="text-2xl font-bold text-red-600">
              {pages.filter(p => !p.is_active).length}
            </p>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <p className="text-sm text-gray-600">Page Types</p>
            <p className="text-2xl font-bold">
              {[...new Set(pages.map(p => p.page_type))].length}
            </p>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-8">Loading pages...</div>
        ) : (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Title
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Slug
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {pages.map((page) => (
                    <tr key={page.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div className="font-medium text-gray-900">{page.title}</div>
                        <div className="text-sm text-gray-500">
                          {page.hero_headline && (
                            <div className="truncate max-w-xs" title={page.hero_headline}>
                              "{page.hero_headline}"
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className="px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                          {page.page_type}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <code className="text-sm text-gray-600">/{page.slug}</code>
                      </td>
                      <td className="px-6 py-4">
                        <button
                          onClick={() => toggleStatus(page.id, page.is_active)}
                          className={`px-3 py-1 rounded-full text-xs font-semibold cursor-pointer ${
                            page.is_active
                              ? 'bg-green-100 text-green-800 hover:bg-green-200'
                              : 'bg-red-100 text-red-800 hover:bg-red-200'
                          }`}
                        >
                          {page.is_active ? 'Active' : 'Inactive'}
                        </button>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleEdit(page.id)}
                            className="text-blue-600 hover:text-blue-900 text-sm font-medium px-3 py-1 bg-blue-50 rounded hover:bg-blue-100"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => viewPage(page.slug)}
                            className="text-green-600 hover:text-green-900 text-sm font-medium px-3 py-1 bg-green-50 rounded hover:bg-green-100"
                          >
                            View
                          </button>
                          <button
                            onClick={() => handleDelete(page.id)}
                            className="text-red-600 hover:text-red-900 text-sm font-medium px-3 py-1 bg-red-50 rounded hover:bg-red-100"
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Instructions */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
          <h3 className="font-semibold text-blue-800 mb-2">How to Manage Pages</h3>
          <ul className="space-y-2 text-sm text-blue-700">
            <li>• <strong>Edit</strong> - Click edit to modify page content, hero section, and SEO settings</li>
            <li>• <strong>Activate/Deactivate</strong> - Click the status button to show/hide pages on the website</li>
            <li>• <strong>View</strong> - Open the page in a new tab to preview it</li>
            <li>• <strong>Delete</strong> - Permanently remove a page (use with caution)</li>
            <li>• <strong>Page Types</strong>: Home, About, Consulting, Coaching, Programs, Corporate Training, Insights, Contact</li>
          </ul>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-semibold mb-4">Confirm Delete</h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete this page? This action cannot be undone.
            </p>
            <div className="flex justify-end space-x-4">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  handleDelete(deleteConfirm);
                  setDeleteConfirm(null);
                }}
                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}

export default AdminPages;